import type { GameState } from "./types";

export type PersonalBests = Pick<GameState, "bestScore" | "bestRound" | "bestMaxFlow">;

const STORAGE_KEY = "tavela.personalBests";

const DEFAULT_PERSONAL_BESTS: PersonalBests = {
  bestScore: 0,
  bestRound: 0,
  bestMaxFlow: 0
};

function getLocalStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function sanitizeBestValue(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return 0;
  }

  return Math.max(0, Math.trunc(value));
}

function sanitizePersonalBests(value: unknown): PersonalBests {
  if (typeof value !== "object" || value === null) {
    return { ...DEFAULT_PERSONAL_BESTS };
  }

  const candidate = value as Partial<Record<keyof PersonalBests, unknown>>;

  return {
    bestScore: sanitizeBestValue(candidate.bestScore),
    bestRound: sanitizeBestValue(candidate.bestRound),
    bestMaxFlow: sanitizeBestValue(candidate.bestMaxFlow)
  };
}

export function loadPersonalBests(): PersonalBests {
  const storage = getLocalStorage();
  if (storage === null) {
    return { ...DEFAULT_PERSONAL_BESTS };
  }

  try {
    const storedValue = storage.getItem(STORAGE_KEY);
    if (storedValue === null) {
      return { ...DEFAULT_PERSONAL_BESTS };
    }

    return sanitizePersonalBests(JSON.parse(storedValue));
  } catch {
    return { ...DEFAULT_PERSONAL_BESTS };
  }
}

export function savePersonalBests(personalBests: PersonalBests): void {
  const storage = getLocalStorage();
  if (storage === null) {
    return;
  }

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(sanitizePersonalBests(personalBests)));
  } catch {
    // Ignore storage write failures so gameplay remains unaffected.
  }
}
